import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { RaceMatrixData } from "./types"

interface RaceMatrixTableProps {
  raceMatrix: RaceMatrixData
}

const winRateColor = (winRateCell: string) => {
  if (winRateCell === "-") return "text-muted-foreground dark:text-gray-300"
  const value = Number(winRateCell)
  if (value >= 60) return "text-green-600"
  if (value >= 40) return "text-yellow-600"
  return "text-red-600"
}

export const RaceMatrixTable = ({ raceMatrix }: RaceMatrixTableProps) => (
  <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
    <CardHeader>
      <CardTitle className="text-base sm:text-lg text-gray-900 dark:text-gray-100">
        Análisis Raza vs Raza
      </CardTitle>
      <p className="text-sm text-muted-foreground dark:text-gray-300">
        Efectividad de enfrentamientos entre razas
      </p>
    </CardHeader>

    <CardContent className="p-2 sm:p-6 sm:pt-2 overflow-x-auto">
      {Object.keys(raceMatrix.matrix).length > 0 ? (
        <>
          {/* Glosa explicativa */}
          <div className="mb-4 text-sm text-muted-foreground dark:text-gray-300">
            <p>
              La siguiente tabla muestra la tasa de victorias de cada raza (<strong>filas</strong>)
              al enfrentarse contra otra raza (<strong>columnas</strong>). Un valor del{" "}
              <span className="text-green-600 font-medium mx-1">60% o más</span> indica una clara
              ventaja, entre <span className="text-yellow-600 font-medium mx-1">40% y 59%</span>
              refleja un enfrentamiento equilibrado, y menos del{" "}
              <span className="text-red-600 font-medium mx-1">40%</span> muestra desventaja.
            </p>
            <p className="mt-2 italic">
              En la diagonal (raza vs sí misma) se muestran enfrentamientos espejo. Estos suelen
              acercarse al <strong>50%</strong> y reflejan únicamente la distribución de victorias y
              empates en esos duelos internos.
            </p>
          </div>

          <div className="overflow-x-auto">
            <Table className="min-w-full border rounded-lg border-gray-200 dark:border-gray-700">
              <TableHeader>
                <TableRow className="bg-muted/50 dark:bg-gray-700">
                  <TableHead className="font-semibold text-gray-900 dark:text-gray-100">
                    Raza
                  </TableHead>
                  {Object.keys(raceMatrix.matrix).map((race) => (
                    <TableHead
                      key={race}
                      className="text-center font-semibold text-gray-900 dark:text-gray-100"
                    >
                      {race}
                    </TableHead>
                  ))}
                  <TableHead className="text-center font-semibold text-gray-900 dark:text-gray-100">
                    Victorias Totales
                  </TableHead>
                  <TableHead className="text-center font-semibold text-gray-900 dark:text-gray-100">
                    % Global
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {raceMatrix.summary.map(({ race, wins, winRate }, idx) => (
                  <TableRow
                    key={race}
                    className={
                      idx % 2 === 0 ? "bg-white dark:bg-gray-800" : "bg-muted/20 dark:bg-gray-700"
                    }
                  >
                    {/* Nombre de raza */}
                    <TableHead className="text-center font-semibold px-2 py-2 text-gray-900 dark:text-gray-100">
                      <div className="whitespace-nowrap">{race}</div>
                    </TableHead>

                    {/* Celdas contra otras razas */}
                    {Object.keys(raceMatrix.matrix).map((raceCol) => {
                      const data = raceMatrix.matrix[race][raceCol]
                      const winRateCell =
                        data?.total > 0 ? ((data.wins / data.total) * 100).toFixed(1) : "-"

                      return (
                        <TableCell key={raceCol} className="text-center px-3 py-2">
                          <span className={`font-medium ${winRateColor(winRateCell)}`}>
                            {winRateCell}
                          </span>
                        </TableCell>
                      )
                    })}
                    {/* Totales */}
                    <TableCell className="text-center font-medium text-green-600 px-3 py-2">
                      {wins}
                    </TableCell>
                    <TableCell className="text-center font-medium px-3 py-2">{winRate}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground dark:text-gray-300">
          No hay datos de enfrentamientos para la ronda y mesa seleccionadas.
        </div>
      )}
    </CardContent>
  </Card>
)
